import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { from, lastValueFrom, of, timer } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { CustomerEntity } from '../../../../models/entities/customer';
import { TrainerEntity } from '../../../../models/entities/trainer';
import { NewCustomerDrawerComponent } from '../../../shared/new-customer-drawer/new-customer-drawer.component';
import { CourseEntity } from '../../../../models/entities/course';
import { RequisitionMovement } from '../../../../models/entities/requisition-model';
import { RequisitionData } from '../../../../models/wizard-plugin-models/requisition-data';
import { TrainingScheduleData } from '../../../../models/wizard-plugin-models/training-schedules-data';
import { AdditionalInformationData } from '../../../../models/wizard-plugin-models/additional-information-data';
import { IRequisitionEntityService } from '../../../../interfaces/entities/requisition-entity-service-interface';
import { ICustomerEntityService } from '../../../../interfaces/entities/customer-entity-service.interface';
import { ITrainerEntityService } from '../../../../interfaces/entities/trainer-entity-service.interface';
import {
  CUSTOMER_ENTITY_SERVICE_TOKEN,
  REQUISITION_ENTITY_SERVICE_TOKEN,
  TRAINER_ENTITY_SERVICE_TOKEN,
} from '../../../../injection-tokens/entity-service-tokens';
import { BusinessProcessEntityService } from '../../../../services/settings/businessprocess-entity.service';
import { LoginEntityService } from '../../../../services/entity-services/login-entity.service';
import { Log, IShortLogger } from '../../../../services/logger.service';

/** Validates that a FormArray has at least one fully-filled date range group. */
function atLeastOneCompleteDateRange(): ValidatorFn {
  return (ctrl: AbstractControl): ValidationErrors | null => {
    const arr = ctrl as FormArray;
    if (!arr || arr.length === 0) return { noDateRanges: true };
    const hasComplete = arr.controls.some(g => {
      const v = g.value;
      return v.startDate && v.endDate && v.startTime && v.endTime;
    });
    return hasComplete ? null : { noCompleteRange: true };
  };
}

/** Validates that no two date range slots have overlapping calendar dates.
 * When two slots share exactly one boundary day (end of A == start of B),
 * time intervals are checked — they are valid if the times don't overlap on
 * that shared day. When they share more than one day, it is always invalid.
 */
function noOverlappingSlots(): ValidatorFn {
  return (ctrl: AbstractControl): ValidationErrors | null => {
    const arr = ctrl as FormArray;
    if (arr.length < 2) return null;

    const toDay = (d: any): number => {
      if (!d) return NaN;
      const dt = d instanceof Date ? d : new Date(d);
      return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()).getTime();
    };
    const toMins = (t: string): number => {
      if (!t) return NaN;
      const [h, m] = t.split(':').map(Number);
      return h * 60 + (m || 0);
    };
    const DAY = 86_400_000;

    for (let i = 0; i < arr.length; i++) {
      for (let j = i + 1; j < arr.length; j++) {
        const a = arr.at(i).value;
        const b = arr.at(j).value;
        if (!a.startDate || !a.endDate || !b.startDate || !b.endDate) continue;
        const aS = toDay(a.startDate), aE = toDay(a.endDate);
        const bS = toDay(b.startDate), bE = toDay(b.endDate);
        if (isNaN(aS) || isNaN(aE) || isNaN(bS) || isNaN(bE)) continue;

        const overlapStart = Math.max(aS, bS);
        const overlapEnd   = Math.min(aE, bE);
        if (overlapStart > overlapEnd) continue; // no date overlap at all

        const sharedDays = Math.round((overlapEnd - overlapStart) / DAY) + 1;

        if (sharedDays > 1) {
          // Multiple shared days — always a conflict regardless of time.
          return { slotOverlap: { slotA: i + 1, slotB: j + 1 } };
        }

        // Exactly one shared day — check if the time intervals overlap on it.
        const aSm = toMins(a.startTime), aEm = toMins(a.endTime);
        const bSm = toMins(b.startTime), bEm = toMins(b.endTime);
        if (!isNaN(aSm) && !isNaN(aEm) && !isNaN(bSm) && !isNaN(bEm)) {
          // Strict overlap: [aSm, aEm) ∩ [bSm, bEm) is non-empty
          if (aSm < bEm && bSm < aEm) {
            return { slotOverlap: { slotA: i + 1, slotB: j + 1 } };
          }
        }
      }
    }
    return null;
  };
}

@Component({
  selector: 'app-requisition-create-wizard',
  templateUrl: './requisition-create-wizard.component.html',
  styleUrl: './requisition-create-wizard.component.css',
})
export class RequisitionCreateWizardComponent implements OnInit {
  private readonly logger: IShortLogger<RequisitionCreateWizardComponent> =
    Log.create(RequisitionCreateWizardComponent);

  // ── Forms ──────────────────────────────────────────────────────────────────
  detailsForm!: FormGroup;
  scheduleForm!: FormGroup;
  additionalForm!: FormGroup;
  forwardForm!: FormGroup;

  // ── Customer ───────────────────────────────────────────────────────────────
  @ViewChild('customerDrawer') customerDrawer!: NewCustomerDrawerComponent;
  selectedCustomer: CustomerEntity | null = null;
  customers: CustomerEntity[] = [];
  filteredCustomers: CustomerEntity[] = [];
  customerSearch = '';
  customerDrawerOpen = false;
  customerNameTaken = false;
  customerNameChecking = false;

  readonly customerTypeValues = [
    'Corporate',
    'Individual',
    'Educational Institution',
    'Government',
    'Non-profit',
  ];
  filteredCustomerTypes: string[] = [];
  customerTypeSearch = '';

  // ── Trainer ────────────────────────────────────────────────────────────────
  selectedTrainer: TrainerEntity | null = null;
  trainers: TrainerEntity[] = [];
  filteredTrainers: TrainerEntity[] = [];
  trainerSearch = '';

  // ── Forward ────────────────────────────────────────────────────────────────
  readonly FORWARD_ACTIVITY_CODE = 'LRC Filling';
  teams: any[] = [];
  users: any[] = [];

  // ── State ──────────────────────────────────────────────────────────────────
  readonly today = new Date();
  isSubmitting = false;
  /** Index of the currently expanded session slot accordion panel. */
  expandedSlotIndex = 0;

  constructor(
    private readonly fb: FormBuilder,
    readonly router: Router,
    private readonly snackBar: MatSnackBar,
    private readonly businessProcessService: BusinessProcessEntityService,
    private readonly loginService: LoginEntityService,
    @Inject(REQUISITION_ENTITY_SERVICE_TOKEN)
    private readonly reqService: IRequisitionEntityService,
    @Inject(CUSTOMER_ENTITY_SERVICE_TOKEN)
    private readonly customerService: ICustomerEntityService,
    @Inject(TRAINER_ENTITY_SERVICE_TOKEN)
    private readonly trainerService: ITrainerEntityService,
  ) {}

  ngOnInit(): void {
    this.filteredCustomerTypes = [...this.customerTypeValues];
    this.buildForms();
    this.loadCustomers();
    this.loadTrainers();
    this.loadActivities();
    this.forwardForm.get('team')?.valueChanges.subscribe(team => this.onTeamChange(team));

  }

  // ── Form builders ──────────────────────────────────────────────────────────
  private buildForms(): void {
    this.detailsForm = this.fb.group({
      enquiryId: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9_-]+$/)], [this.enquiryDuplicateValidator()]],
      customerType: ['', Validators.required],
      customerId: ['', Validators.required],
    });

    this.scheduleForm = this.fb.group({
      batchTitle: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9_.\- ]+$/)]],
      trainerId: [''],
      dateRanges: this.fb.array([this.newDateRangeGroup()], [atLeastOneCompleteDateRange(), noOverlappingSlots()]),
    });

    this.additionalForm = this.fb.group({
      specialRequirements: [''],
      finalComments: [''],
    });

    this.forwardForm = this.fb.group({
      team: ['', Validators.required],
      user: ['', Validators.required],
      remarks: ['', Validators.required],
    });
  }

  newDateRangeGroup(): FormGroup {
    return this.fb.group({
      slotTitle:  ['', Validators.pattern(/^[a-zA-Z0-9_.\- ]*$/)],
      startDate: ['', Validators.required],
      endDate:   ['', Validators.required],
      startTime: ['09:00', Validators.required],
      endTime:   ['17:00', Validators.required],
    });
  }

  get dateRanges(): FormArray {
    return this.scheduleForm.get('dateRanges') as FormArray;
  }

  addDateRange(): void {
    this.dateRanges.push(this.newDateRangeGroup());
    this.expandedSlotIndex = this.dateRanges.length - 1;
  }

  removeDateRange(index: number): void {
    if (this.dateRanges.length > 1) {
      this.dateRanges.removeAt(index);
      this.expandedSlotIndex = Math.min(this.expandedSlotIndex, this.dateRanges.length - 1);
    }
  }

  /** True when slot at index has a non-empty title that contains invalid characters. */
  isSlotTitleInvalid(index: number): boolean {
    const ctrl = this.dateRanges.at(index).get('slotTitle');
    return !!ctrl && ctrl.touched && ctrl.hasError('pattern');
  }

  /** Block keystrokes that don't match the allowed slot-title character set. */
  filterSlotTitleKey(event: KeyboardEvent): void {
    // Allow control keys (backspace, delete, arrows, home/end, tab, etc.)
    if (event.key.length !== 1) return;
    const allowed = /^[a-zA-Z0-9_.\- ]$/;
    if (!allowed.test(event.key)) {
      event.preventDefault();
    }
  }

  /** Strip invalid characters from pasted text before it lands in the input. */
  filterSlotTitlePaste(event: ClipboardEvent, index: number): void {
    event.preventDefault();
    const raw = event.clipboardData?.getData('text') ?? '';
    const cleaned = raw.replace(/[^a-zA-Z0-9_.\- ]/g, '');
    const ctrl = this.dateRanges.at(index).get('slotTitle');
    if (!ctrl) return;
    const input = event.target as HTMLInputElement;
    const start = input.selectionStart ?? ctrl.value.length;
    const end = input.selectionEnd ?? ctrl.value.length;
    const current: string = ctrl.value ?? '';
    const next = current.slice(0, start) + cleaned + current.slice(end);
    ctrl.setValue(next.slice(0, 40));
    // Restore caret position after setValue re-renders
    const newCaret = start + cleaned.length;
    setTimeout(() => input.setSelectionRange(newCaret, newCaret));
  }

  /** Trim leading/trailing whitespace from a slot title on blur; clear if only spaces. */
  trimSlotTitle(index: number): void {
    const ctrl = this.dateRanges.at(index).get('slotTitle');
    if (ctrl) {
      const trimmed = (ctrl.value ?? '').trim();
      ctrl.setValue(trimmed);
      ctrl.markAsTouched();
    }
  }

  /** True when the slot at `index` has the same non-empty title as any other slot. */
  isSlotTitleDuplicate(index: number): boolean {
    const mine = (this.dateRanges.at(index).value.slotTitle || '').trim().toLowerCase();
    if (!mine) return false;
    return this.dateRanges.controls.some((c, i) =>
      i !== index && (c.value.slotTitle || '').trim().toLowerCase() === mine,
    );
  }

  /** True when any two slots share the same non-empty title. */
  hasDuplicateSlotTitles(): boolean {
    const titles = this.dateRanges.controls
      .map(c => (c.value.slotTitle || '').trim().toLowerCase())
      .filter(t => t !== '');
    return titles.length !== new Set(titles).size;
  }

  /** Returns true when the slot at `index` is one of the two overlapping slots. */
  isSlotInOverlap(index: number): boolean {
    const err = this.dateRanges.errors?.['slotOverlap'] as { slotA: number; slotB: number } | undefined;
    return !!err && (err.slotA === index + 1 || err.slotB === index + 1);
  }

  // ── Async validator ─────────────────────────────────────────────────────────
  private enquiryDuplicateValidator(): AsyncValidatorFn {
    return (ctrl: AbstractControl) => {
      if (!ctrl.value) return of(null);
      return timer(500).pipe(
        switchMap(() => from(this.reqService.checkDuplicateEnquiry(ctrl.value))),
        map((res: any) => (res?.exists ? { duplicateEnquiry: true } : null)),
      );
    };
  }

  // ── Step navigation helpers ─────────────────────────────────────────────────
  touchStep1(): void {
    this.detailsForm.markAllAsTouched();
  }

  touchStep2(): void {
    this.scheduleForm.markAllAsTouched();
  }

  // ── Customer ────────────────────────────────────────────────────────────────
  private loadCustomers(): void {
    this.customerService.getAllCustomers().then(all => {
      this.customers = all;
      this.filteredCustomers = this.getFilteredCustomerList(this.customerSearch);
    });
  }

  filterCustomers(query: string | any): void {
    if (typeof query !== 'string') return;
    this.filteredCustomers = this.getFilteredCustomerList(query);
  }

  onCustomerInputFocus(): void {
    this.filteredCustomers = this.getFilteredCustomerList('');
  }

  /** Blocks characters not in [a-zA-Z0-9_-] from being typed into the Enquiry No. field. */
  filterEnquiryKey(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (event.key.length === 1 && !/^[a-zA-Z0-9_-]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  /** If user types free text in Customer Type and blurs without selecting, clear the value and mark invalid. */
  validateCustomerTypeOnBlur(): void {
    const typed = (this.customerTypeSearch ?? '').trim();
    const isValid = this.customerTypeValues.some(t => t === typed);
    if (!isValid) {
      this.customerTypeSearch = '';
      this.detailsForm.patchValue({ customerType: '' });
    }
    this.detailsForm.get('customerType')?.markAsTouched();
  }

  filterCustomerTypes(query: string | any): void {
    if (typeof query !== 'string') return;
    const q = query.toLowerCase();
    this.filteredCustomerTypes = q
      ? this.customerTypeValues.filter(t => t.toLowerCase().includes(q))
      : [...this.customerTypeValues];
  }

  onCustomerSelected(customer: CustomerEntity): void {
    this.selectedCustomer = customer;
    this.customerSearch = customer.name;
    this.detailsForm.patchValue({ customerId: customer.id });
    this.filteredCustomers = this.getFilteredCustomerList(this.customerSearch);
  }

  onCustomerTypeSelected(type: string): void {
    this.customerTypeSearch = type;
    this.detailsForm.patchValue({ customerType: type });
    this.detailsForm.get('customerType')?.markAsTouched();
  }

  openCustomerDrawer(): void {
    this.customerNameTaken = false;
    this.customerNameChecking = true;
    this.customerDrawerOpen = true;

    // Refresh active customer list so duplicate check reflects latest data.
    this.customerService.getAllCustomers()
      .then(all => {
        this.customers = all;
        this.filteredCustomers = this.getFilteredCustomerList(this.customerSearch);
      })
      .finally(() => {
        this.customerNameChecking = false;
      });
  }

  closeCustomerDrawer(): void {
    this.customerDrawerOpen = false;
    this.customerNameTaken = false;
    this.customerNameChecking = false;
  }

  onNewCustomerNameChange(value: string): void {
    const trimmed = (value ?? '').trim();
    if (!trimmed) {
      this.customerNameTaken = false;
      this.customerNameChecking = false;
      return;
    }

    this.customerNameTaken = this.isCustomerNameDuplicate(trimmed);
    this.customerNameChecking = false;
  }

  onNewCustomerSaved(entity: CustomerEntity): void {
    this.upsertCustomer(entity);
    this.onCustomerSelected(entity);
  }

  private upsertCustomer(customer: CustomerEntity): void {
    const id = (customer.id ?? '').trim();
    const index = this.customers.findIndex(c => (c.id ?? '').trim() === id);
    if (index === -1) {
      this.customers.push(customer);
    } else {
      this.customers[index] = customer;
    }
    this.filteredCustomers = this.getFilteredCustomerList(this.customerSearch);
  }

  private getFilteredCustomerList(query: string): CustomerEntity[] {
    const q = (query ?? '').toLowerCase();
    const selectedId = (this.selectedCustomer?.id ?? '').trim();

    const visible = this.customers.filter(c => {
      const isSelected = selectedId && (c.id ?? '').trim() === selectedId;
      if (isSelected) {
        return false;
      }

      if (!q) {
        return true;
      }

      return (c.name ?? '').toLowerCase().includes(q);
    });

    return visible.slice(0, 20);
  }

  private isCustomerNameDuplicate(value: string): boolean {
    const normalized = (value ?? '').trim().toLowerCase();
    if (!normalized) {
      return false;
    }

    return this.customers.some(c => (c.name ?? '').trim().toLowerCase() === normalized);
  }

  // ── Trainer ─────────────────────────────────────────────────────────────────
  private loadTrainers(): void {
    // Load trainers from the backend (users with role = 'trainer').
    // Falls back gracefully to empty list if the endpoint returns nothing.
    this.loginService.userGetByRole('trainer').subscribe({
      next: (result: any[]) => {
        if (result && result.length > 0) {
          // Backend returns UserEntity[] — map to TrainerEntity shape for display
          this.trainers = result.map((u: any) => {
            const t = new TrainerEntity();
            t.id = u.id ?? u.Id ?? '';
            t.name = u.name ?? u.Name ?? u.userName ?? '?';
            t.email = u.email ?? u.Email ?? '';
            return t;
          });
          this.filteredTrainers = this.trainers.slice(0, 20);
        } else {
          this.trainers = [];
          this.filteredTrainers = [];
        }
      },
      error: () => {
        // No trainer API — leave list empty; field stays optional
        this.trainers = [];
        this.filteredTrainers = [];
      },
    });
  }

  filterTrainers(query: string | any): void {
    if (typeof query !== 'string') return;
    const q = query.toLowerCase();
    this.filteredTrainers = q
      ? this.trainers.filter(t => t.name.toLowerCase().includes(q)).slice(0, 20)
      : this.trainers.slice(0, 20);
  }

  validateTrainerOnBlur(): void {
    const typed = (this.trainerSearch ?? '').trim();
    if (!typed) { this.trainerSearch = ''; return; }
    const match = this.trainers.find(t => t.name.toLowerCase() === typed.toLowerCase());
    if (!match) {
      this.trainerSearch = '';
      this.selectedTrainer = null;
      this.scheduleForm.patchValue({ trainerId: '' });
    }
  }

  onTrainerSelected(trainer: TrainerEntity): void {
    this.selectedTrainer = trainer;
    this.trainerSearch = trainer.name;
    this.scheduleForm.patchValue({ trainerId: trainer.id });
  }

  clearTrainer(): void {
    this.selectedTrainer = null;
    this.trainerSearch = '';
    this.scheduleForm.patchValue({ trainerId: '' });
  }

  /**
   * Converts a customer entity or string to its display name for the autocomplete.
   * Handles the case where the option value is the full customer object.
   */
  displayCustomerName(customer: CustomerEntity | string | null): string {
    if (!customer) return '';
    if (typeof customer === 'string') return customer;
    return (customer as CustomerEntity).name || '';
  }

  /**
   * Converts a trainer entity or string to its display name for the autocomplete.
   * Handles the case where the option value is the full trainer object.
   */
  displayTrainerName(trainer: TrainerEntity | string | null): string {
    if (!trainer) return '';
    if (typeof trainer === 'string') return trainer;
    return (trainer as TrainerEntity).name || '';
  }

  // ── Forward ─────────────────────────────────────────────────────────────────
  private loadActivities(): void {
    this.businessProcessService.getAllBusinessProcess().subscribe({
      next: (result: any[]) => {
        const activities: any[] =
          result.find((bp: any) => bp.name === 'Requisitions')?.activities || [];
        const lrc = activities.find(
          (a: any) =>
            a.code?.toLowerCase().trim() === this.FORWARD_ACTIVITY_CODE.toLowerCase().trim(),
        );
        if (lrc?.actions?.[0]?.rights?.teams) {
          this.teams = lrc.actions[0].rights.teams;
        } else {
          this.logger.w('LRC Filling activity or teams not found in business process configuration');
        }
      },
      error: err => this.logger.e('Failed to load activities:', err),
    });
  }

  private onTeamChange(team: string): void {
    this.users = [];
    this.forwardForm.patchValue({ user: '' }, { emitEvent: false });
    if (team) {
      this.loginService.usersGetByTeam(team).subscribe({
        next: (result: any[]) => {
          this.users = result;
        },
        error: err => this.logger.e('Failed to load users for team:', err),
      });
    }
  }

  /**
   * Serialises a Date (or bare YYYY-MM-DD string) to a YYYY-MM-DD string using LOCAL
   * calendar date parts, so the backend receives the exact day the user picked regardless
   * of timezone. Mirrors schedule-settings.component.ts's formatLocalDate().
   */
  private formatLocalDate(d: any): string | null {
    if (!d) return null;
    if (d instanceof Date) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }
    return String(d).substring(0, 10) || null;
  }

  // ── Payload assembly ────────────────────────────────────────────────────────
  private buildDraft(): Record<string, any> {
    const d = this.detailsForm.value;
    const a = this.additionalForm.value;

    // Hardcoded "Custom Course" default — matches live MongoDB data (Id = "-1")
    const course = new CourseEntity();
    course.id = '-1';
    course.name = 'Custom Course';
    course.code = 'Custom';

    // Date ranges as plain objects — JSON-serialises identically to DateRange class.
    // startDate/endDate are sent as bare YYYY-MM-DD strings (local calendar date parts),
    // NOT raw Date objects — Angular's HttpClient JSON-serialises a raw Date via
    // .toISOString() (always UTC), which shifts any UTC+ timezone user's local-midnight
    // pick back to the previous UTC calendar day (e.g. IST midnight becomes 18:30 UTC the
    // day before), causing ClassModel.Status to misclassify the resulting class as
    // OnGoing/Completed a day early. See formatLocalDate().
    const dateRanges = this.dateRanges.controls.map(ctrl => {
      const v = ctrl.value;
      return {
        startDate: this.formatLocalDate(v.startDate),
        endDate: this.formatLocalDate(v.endDate),
        startTime: v.startTime,
        endTime: v.endTime,
        sessionTitle: (v.slotTitle || '').trim(),
      };
    });

    // Single training schedule entry
    const schedule = new TrainingScheduleData();
    schedule.title = this.scheduleForm.value.batchTitle;
    schedule.course = course;
    schedule.participants = [];
    schedule.trainers = this.selectedTrainer
      ? [{ id: this.selectedTrainer.id, name: this.selectedTrainer.name }]
      : [];
    schedule.dateRanges = dateRanges as any;
    schedule.resourceTemplateIds = [];
    schedule.accessViaInvitedEmailOnly = true;

    // Requisition header block
    const reqData = new RequisitionData();
    reqData.enquiryId = d.enquiryId;
    reqData.docDate = new Date();
    reqData.status = 'Draft';
    const cd = reqData.customerDetails;
    cd.id = this.selectedCustomer!.id;
    cd.name = this.selectedCustomer!.name;
    cd.customerType = d.customerType;
    cd.gstNumber = this.selectedCustomer!.gstNumber || '';
    cd.address = this.selectedCustomer!.address || '';
    Object.assign(cd.contactPerson, this.selectedCustomer!.contactPerson);

    // Additional info
    const additionalData = new AdditionalInformationData();
    additionalData.specialRequirements = a.specialRequirements || '';
    additionalData.finalComments = a.finalComments || '';

    // Full RequisitionDraft shape.
    // IMPORTANT: Data["TrainingSchedulesData"] must be a FLAT ARRAY.
    // The backend does: JsonSerializer.Deserialize<List<TrainingSchedulesData>>(...)
    // on model.Data[nameof(TrainingSchedulesData)], so the value cannot be a wrapper object.
    const userId = localStorage.getItem('userId') ?? '';
    return {
      CreatedBy: userId,
      UpdatedBy: userId,
      CreatedOn: new Date(),
      UpdatedOn: new Date(),
      Data: {
        RequisitionData: reqData,
        TrainingSchedulesData: [schedule], // flat array — NOT new TrainingSchedulesData()
        AdditionalInformationData: additionalData,
      },
    };
  }

  // ── Submit ──────────────────────────────────────────────────────────────────
  async submit(): Promise<void> {
    if (this.forwardForm.invalid || this.isSubmitting) return;
    this.isSubmitting = true;

    try {
      const userId = localStorage.getItem('userId') ?? '';
      const draft = this.buildDraft();
      // Cast via unknown — the service accepts any object; Requisition type is too narrow for a draft
      const created: any = await this.reqService.createRequisition(draft as unknown as any, userId);

      // Backend may return PascalCase Id — handle both
      const createdId: string = created?.id ?? created?.Id ?? '';

      const movement: RequisitionMovement = {
        requisitionId: createdId,
        purpose: this.FORWARD_ACTIVITY_CODE,
        purposeCode: this.FORWARD_ACTIVITY_CODE,
        forwardToTeam: this.forwardForm.value.team,
        forwardToUser: this.forwardForm.value.user,
        forwardToTeamId:
          this.teams.find((t: any) => t.team === this.forwardForm.value.team)?.teamId ?? '',
        forwardToUserId:
          this.users.find((u: any) => u.name === this.forwardForm.value.user)?.id ?? '',
        forwardFromTeam: localStorage.getItem('team') ?? '',
        forwardFromTeamId: localStorage.getItem('teamId') ?? '',
        forwardFromUser: localStorage.getItem('userName') ?? '',
        forwardFromUserId: userId,
        forwardedOn: new Date(),
        remarks: this.forwardForm.value.remarks,
        isActive: true,
        isRead: false,
        isPurposeDone: false,
        readDate: null,
        srNo: 0,
      } as any;

      await lastValueFrom(this.reqService.forwardRequisition(movement));

      this.snackBar.open('Requisition created and forwarded successfully.', 'OK', {
        duration: 3000,
      });
      this.router.navigate(['/requisitions']);
    } catch (err) {
      this.logger.e('Failed to submit requisition:', err);
      this.snackBar.open('Failed to create requisition. Please try again.', 'Dismiss', {
        duration: 4000,
      });
    } finally {
      this.isSubmitting = false;
    }
  }

  // ── Review helpers ──────────────────────────────────────────────────────────
  get reviewDateRanges(): any[] {
    return this.dateRanges.controls.map(c => c.value);
  }
  /** Converts a 24-hour "HH:MM" string to "h:mm AM/PM" for display. */
  formatTime12h(time: string): string {
    if (!time) return '—';
    const [hStr, mStr] = time.split(':');
    const h24 = parseInt(hStr, 10);
    const m   = parseInt(mStr, 10) || 0;
    const period = h24 < 12 ? 'AM' : 'PM';
    const h12   = h24 % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${period}`;
  }}
